import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import CustomDropdown from "../Components/CustomDropdown";
import SkipCheckbox from "../Components/SkipCheckbox";
import { words } from "../translations";
import { modularSelectCommentTypeQuestion } from "constants/questionTypes";

const SingleSelectFeedback = ({
  questionSequenceMap,
  direction,
  handleNext,
  handleBack,
  saveUpdatedAnswers,
  currentQuestionDet,
  language,
  disableNext,
  customHeader,
}) => {
  const [isNA, setIsNA] = useState(false);
  const [selectedOption, setSelectedOption] = useState(null);
  const [followUpResponse, setFollowUpResponse] = useState("");
  const [showFollowUp, setShowFollowUp] = useState(false);

  const askOnRefResp = questionSequenceMap[currentQuestionDet?.askOnRefQID];
  // Map options from feedback_builder.select to CustomDropdown shape
  const selectConfig = currentQuestionDet?.question?.select;

  const groupTitles = useMemo(() => selectConfig?.group_titles || {}, [selectConfig]);
  const getGroupTitle = (groupKey) => {
    if (!groupKey) return "";
    const localized = groupTitles?.[groupKey];
    return (localized?.[language] && String(localized?.[language]).trim())
      ? localized[language]
      : (localized?.EN || groupKey);
  };
  const options = useMemo(() => {
    const raw = Array.isArray(selectConfig?.options) ? selectConfig.options : [];
    return raw.map((opt) => ({
      id: opt?.id,
      group: opt?.group,
      name: opt?.label?.[language] || opt?.label?.EN || opt?.label, // Extract localized name
      label: opt?.label, // Keep original label object for reference
      followUp: opt?.follow_up, // map through follow up map if present
      // Add language-specific properties for CustomDropdown compatibility
      fr: opt?.label?.FR,
      es: opt?.label?.ES,
      por: opt?.label?.POR,
    }));
  }, [selectConfig, language]);

  useEffect(() => {
    if (
      currentQuestionDet?.askOnRef?.value?.length > 0 &&
      Number.isInteger(askOnRefResp?.response?.response)
    ) {
      let _refQuesResp = askOnRefResp?.response?.response;
      if (!isNaN(_refQuesResp)) {
        _refQuesResp = parseInt(_refQuesResp, 10);
      }
      const [min, max] = currentQuestionDet?.askOnRef?.value || [];
      const outOfRange = !(_refQuesResp >= min && _refQuesResp <= max);
      if (outOfRange) {
        if (direction === "slide-back") {
          handleBack();
        } else {
          const QuesResp = {
            ques_sort_order: currentQuestionDet?.id,
            comments: `askOnRefResp - out of range skip [checking for ${_refQuesResp} in ${min},${max}]`,
            ques_order: currentQuestionDet?.id,
            response: null,
            scale: currentQuestionDet?.scale,
            question: currentQuestionDet?.question?.question,
            fr: currentQuestionDet?.question?.fr,
            es: currentQuestionDet?.question?.es,
            por: currentQuestionDet?.question?.por,
            question_type: currentQuestionDet?.quesType,
            order: currentQuestionDet?.id,
            clicked: true,
            skip: true,
            s: currentQuestionDet?.s,
            q: currentQuestionDet?.q,
            id: currentQuestionDet?.id,
          };
          saveUpdatedAnswers(
            currentQuestionDet?.id,
            QuesResp,
            modularSelectCommentTypeQuestion
          );
          handleNext();
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentQuestionDet, askOnRefResp]);

  useEffect(() => {
    // initialize from existing response
    const resp = currentQuestionDet?.response?.response;

    const normalize = (v) =>
      (v ?? "")
        .toString()
        .trim()
        .toLowerCase();

    const tryFindByAnyLabel = (value) => {
      const target = normalize(value);
      return (
        options.find((o) => {
          const candidates = [
            o?.name,
            o?.label?.EN,
            o?.label?.FR,
            o?.label?.ES,
            o?.label?.POR,
            typeof o?.label === "string" ? o.label : undefined,
          ];
          return candidates.some((c) => normalize(c) === target);
        }) || null
      );
    };

    if (resp === 'N/A' || resp === 'SKIP') {
      setSelectedOption(null);
      setIsNA(true);
      return;
    }

    // If response matches option id
    const byId = options.find((o) => {
      if (o?.id == null) return false;
      const respNum = Number(resp);
      if (!Number.isNaN(respNum)) return Number(o.id) === respNum;
      return String(o.id) === String(resp);
    });
    if (byId) {
      setSelectedOption(byId);
      setIsNA(false);
      // hydrate follow-up response if stored
      const storedFU = currentQuestionDet?.response?.follow_up_response;
      setFollowUpResponse(typeof storedFU === 'string' ? storedFU : "");
      return;
    }

    // Match by any localized label
    if (typeof resp === 'string' && resp) {
      const found = tryFindByAnyLabel(resp);
      setSelectedOption(found);
      setIsNA(false);
      const storedFU = currentQuestionDet?.response?.follow_up_response;
      setFollowUpResponse(typeof storedFU === 'string' ? storedFU : "");
      return;
    }

    // When response is null or sentinel, try hydrate from selected_option_id
    const selId = currentQuestionDet?.response?.selected_option_id;
    if ((resp === null || resp === undefined || resp === 'open_ended') && selId) {
      const foundByStoredId = options.find((o) => String(o?.id) === String(selId));
      if (foundByStoredId) {
        setSelectedOption(foundByStoredId);
        setIsNA(false);
        const storedFU = currentQuestionDet?.response?.follow_up_response;
        setFollowUpResponse(typeof storedFU === 'string' ? storedFU : "");
        return;
      }
    }

    setSelectedOption(null);
    setIsNA(false);
  }, [currentQuestionDet?.response, options, language]);

  // compute if follow-up should be shown for current selection and language
  useEffect(() => {
    const enabled = currentQuestionDet?.question?.option_follow_up?.enabled;
    const hasText = selectedOption?.followUp && (
      (language && selectedOption?.followUp?.[language]) || selectedOption?.followUp?.EN
    );
    setShowFollowUp(Boolean(enabled && hasText && !isNA && selectedOption));
  }, [selectedOption, language, isNA, currentQuestionDet?.question?.option_follow_up?.enabled]);

  // allow Next even when follow-up is blank (skip follow-up)
  useEffect(() => {
    if (typeof disableNext === 'function') disableNext(false);
  }, [disableNext]);

  const getDescription = () => {
    if (language === "FR" && currentQuestionDet?.question?.fr) return currentQuestionDet?.question?.fr;
    if (language === "ES" && currentQuestionDet?.question?.es) return currentQuestionDet?.question?.es;
    if (language === "POR" && currentQuestionDet?.question?.por) return currentQuestionDet?.question?.por;
    return currentQuestionDet?.question?.question;
  };

  const saveResponse = (option) => {
    setIsNA(false);
    setSelectedOption(option);
    const prevSelectedId = currentQuestionDet?.response?.selected_option_id;
    const sameSelection =
      prevSelectedId !== undefined && prevSelectedId !== null && option?.id !== undefined && option?.id !== null
        ? String(prevSelectedId) === String(option.id)
        : false;
    const nextFU = sameSelection
      ? (typeof currentQuestionDet?.response?.follow_up_response === 'string' ? currentQuestionDet?.response?.follow_up_response : "")
      : "";
    setFollowUpResponse(nextFU);
    const QuesResp = {
      ques_sort_order: currentQuestionDet?.id,
      ques_order: currentQuestionDet?.id,
      response: option?.name || option?.label?.[language] || option?.label?.EN || "",
      scale: currentQuestionDet?.scale,
      question: currentQuestionDet?.question?.question,
      fr: currentQuestionDet?.question?.fr,
      es: currentQuestionDet?.question?.es,
      por: currentQuestionDet?.question?.por,
      question_type: currentQuestionDet?.quesType,
      order: currentQuestionDet?.id,
      clicked: true,
      s: currentQuestionDet?.s,
      q: currentQuestionDet?.q,
      id: currentQuestionDet?.id,
      // additional context for follow-up
      selected_option_id: option?.id,
      selected_option_group: option?.group,
      selected_option_labels: option?.label,
      option_follow_up: option?.followUp,
      follow_up_question_display:
        (option?.followUp?.[language] && String(option?.followUp?.[language]).trim())
          ? option?.followUp?.[language]
          : option?.followUp?.EN || "",
      follow_up_response: nextFU,
    };
    saveUpdatedAnswers(
      currentQuestionDet?.id,
      QuesResp,
      modularSelectCommentTypeQuestion
    );
  };

  const handleFollowUpChange = (event) => {
    const value = event?.target?.value ?? "";
    setFollowUpResponse(value);
    if (!selectedOption) return;
    const QuesResp = {
      ques_sort_order: currentQuestionDet?.id,
      ques_order: currentQuestionDet?.id,
      response: selectedOption?.name || selectedOption?.label?.[language] || selectedOption?.label?.EN || "",
      scale: currentQuestionDet?.scale,
      question: currentQuestionDet?.question?.question,
      fr: currentQuestionDet?.question?.fr,
      es: currentQuestionDet?.question?.es,
      por: currentQuestionDet?.question?.por,
      question_type: currentQuestionDet?.quesType,
      order: currentQuestionDet?.id,
      clicked: true,
      s: currentQuestionDet?.s,
      q: currentQuestionDet?.q,
      id: currentQuestionDet?.id,
      selected_option_id: selectedOption?.id,
      selected_option_group: selectedOption?.group,
      selected_option_labels: selectedOption?.label,
      option_follow_up: selectedOption?.followUp,
      follow_up_question_display:
        (selectedOption?.followUp?.[language] && String(selectedOption?.followUp?.[language]).trim())
          ? selectedOption?.followUp?.[language]
          : selectedOption?.followUp?.EN || "",
      follow_up_response: value,
    };
    saveUpdatedAnswers(
      currentQuestionDet?.id,
      QuesResp,
      modularSelectCommentTypeQuestion
    );
  };

  const handleNAClick = () => {
    const next = !isNA;
    setIsNA(next);
    if (next) {
      setSelectedOption(null);
      setFollowUpResponse("");
      setShowFollowUp(false);
    }
    const QuesResp = next
      ? {
          ques_sort_order: currentQuestionDet?.id,
          ques_order: currentQuestionDet?.id,
          response: 'N/A',
          scale: currentQuestionDet?.scale,
          question: currentQuestionDet?.question?.question,
          fr: currentQuestionDet?.question?.fr,
          es: currentQuestionDet?.question?.es,
          por: currentQuestionDet?.question?.por,
          question_type: currentQuestionDet?.quesType,
          order: currentQuestionDet?.id,
          clicked: true,
          s: currentQuestionDet?.s,
          q: currentQuestionDet?.q,
          id: currentQuestionDet?.id,
          follow_up_response: "",
          option_follow_up: undefined,
          selected_option_id: undefined,
          selected_option_group: undefined,
          selected_option_labels: undefined,
          follow_up_question_display: "",
        }
      : {
          ques_sort_order: currentQuestionDet?.id,
          ques_order: currentQuestionDet?.id,
          response: null,
          scale: currentQuestionDet?.scale,
          question: currentQuestionDet?.question?.question,
          fr: currentQuestionDet?.question?.fr,
          es: currentQuestionDet?.question?.es,
          por: currentQuestionDet?.question?.por,
          question_type: currentQuestionDet?.quesType,
          order: currentQuestionDet?.id,
          clicked: false,
          s: currentQuestionDet?.s,
          q: currentQuestionDet?.q,
          id: currentQuestionDet?.id,
          follow_up_response: "",
          option_follow_up: undefined,
          selected_option_id: undefined,
          selected_option_group: undefined,
          selected_option_labels: undefined,
          follow_up_question_display: "",
        };
    saveUpdatedAnswers(currentQuestionDet?.id, QuesResp, modularSelectCommentTypeQuestion);
    if (typeof disableNext === 'function') {
      disableNext(next ? false : true);
    }
  };

  const handleClearSelection = () => {
    setSelectedOption(null);
    setFollowUpResponse("");
    setShowFollowUp(false);
    const QuesResp = {
      ques_sort_order: currentQuestionDet?.id,
      ques_order: currentQuestionDet?.id,
      response: null,
      scale: currentQuestionDet?.scale,
      question: currentQuestionDet?.question?.question,
      fr: currentQuestionDet?.question?.fr,
      es: currentQuestionDet?.question?.es,
      por: currentQuestionDet?.question?.por,
      question_type: currentQuestionDet?.quesType,
      order: currentQuestionDet?.id,
      clicked: false,
      s: currentQuestionDet?.s,
      q: currentQuestionDet?.q,
      id: currentQuestionDet?.id,
      selected_option_id: undefined,
      selected_option_group: undefined,
      selected_option_labels: undefined,
      option_follow_up: undefined,
      follow_up_question_display: "",
      follow_up_response: "",
    };
    saveUpdatedAnswers(currentQuestionDet?.id, QuesResp, modularSelectCommentTypeQuestion);
    if (typeof disableNext === 'function') disableNext(true);
  };

  return (
    <Container>
      {customHeader?.useImageAsQualifier && customHeader?.imageUrl ? (
        <HeaderImage
          src={customHeader.imageUrl}
          alt=""
          $height={customHeader.imageHeight}
        />
      ) : (
        <Title>{words["Feedback"][language]}</Title>
      )}
      <Description>{getDescription()}</Description>

      {!showFollowUp && (
        <CustomDropdown
          id={`sel_${currentQuestionDet?.id}`}
          listId={`list_${currentQuestionDet?.id}`}
          value={selectedOption}
          onSelect={saveResponse}
          language={language}
          options={options}
          groupTitles={groupTitles}
        />
      )}

      {showFollowUp && selectedOption && (
        <FollowUpContainer>
          <SelectedRow>
            <SelectedBadge>
              {selectedOption?.group ? `${getGroupTitle(selectedOption.group)} · ` : ""}
              {selectedOption?.name}
            </SelectedBadge>
            <ChangeSelectionButton onClick={handleClearSelection} aria-label={`${words['Clear'][language]}`}>
              {`× ${words['Clear'][language]}`}
            </ChangeSelectionButton>
          </SelectedRow>
          <FollowUpQuestion>
            {(selectedOption?.followUp?.[language] && String(selectedOption?.followUp?.[language]).trim())
              ? selectedOption?.followUp?.[language]
              : selectedOption?.followUp?.EN || ""}
          </FollowUpQuestion>
          <FollowUpTextarea
            id={`fu_${currentQuestionDet?.id}`}
            value={followUpResponse}
            onChange={handleFollowUpChange}
            placeholder={words["textarea"][language]}
          />
        </FollowUpContainer>
      )}

      {currentQuestionDet?.allowSkip && !showFollowUp && (
        <SkipCheckbox
          id={`skip_${currentQuestionDet?.id}`}
          selected={isNA}
          onClick={handleNAClick}
          language={language}
        />
      )}
    </Container>
  );
};

export default SingleSelectFeedback;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  max-width: 600px;
  width: 100%;
  font-family: "Raleway", sans-serif;
`;

const HeaderImage = styled.img`
  display: block;
  max-width: 100%;
  height: ${props => (props.$height ? `${props.$height}px` : "auto")};
  margin-bottom: 40px;
`;

const Title = styled.div`
  color: #2d70e2;
  text-transform: uppercase;
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 40px;
`;

const Description = styled.div`
  color: #2a3039;
  font-family: "Raleway";
  font-style: normal;
  font-weight: 400;
  font-size: 16px;
  margin-bottom: 34px;
  line-height: 1.6;
  white-space: pre-wrap;
`;

const FollowUpContainer = styled.div`
  width: 100%;
`;

const SelectedRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
  gap: 12px;
`;

const SelectedBadge = styled.div`
  background: #f2f6ff;
  border: 1px solid #d7e4ff;
  color: #2a3039;
  border-radius: 8px;
  padding: 8px 12px;
  font-size: 14px;
`;

const ChangeSelectionButton = styled.button`
  color: #2d70e2;
  background: #eef3ff;
  border: 1px solid #cfe0ff;
  font-weight: 700;
  font-size: 14px;
  cursor: pointer;
  border-radius: 8px;
  padding: 6px 10px;
  line-height: 1;
`;

const FollowUpQuestion = styled.div`
  color: #2a3039;
  font-family: "Raleway";
  font-style: normal;
  font-weight: 500;
  font-size: 16px;
  margin-bottom: 20px;
  line-height: 1.6;
`;

const FollowUpTextarea = styled.textarea`
  display: block;
  width: 100%;
  padding: 12px;
  font-size: 14px;
  font-weight: 400;
  line-height: 1.5;
  color: #2a3039;
  background-color: #fff;
  background-clip: padding-box;
  border: 1px solid #ced4da;
  border-radius: 0.25rem;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
  resize: vertical;
  font-family: "Raleway", sans-serif;

  &:focus {
    color: #495057;
    background-color: #fff;
    border-color: #2d70e2;
    outline: 0;
  }
`;



